import { useCallback, useMemo, useState, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  FilterGroupingEnum,
  Task,
  TaskScopeNameEnum,
  useAssetsSearchListLazyQuery,
  useFormCategoriesSearchListLazyQuery,
  useSectorsSearchListLazyQuery,
  useSitesSearchListLazyQuery,
} from 'graphql-common';
import ModalTableMultiselect, {
  useModalTableAllTabManager,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import ModalTableMultiunselect from '@lib/components/ModalTableMultiselect/ModalTableMultiunselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useModalTableModeManager from '@lib/components/ModalTableMultiselect/hooks/useModalTableModeManager';
import useSelectedFormFieldIds from '@lib/components/ModalTableMultiselect/hooks/useSelectedFormFieldIds';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';
import { getSectorsLoadOptions as getSectorsLoadOptionsFn } from 'utils/fetch/searchLists/sectorsSearchList';
import { getSitesLoadOptions as getSitesLoadOptionsFn } from 'utils/fetch/searchLists/sitesSearchList';
import { getAssetsLoadOptions as getAssetsLoadOptionsFn } from 'utils/fetch/searchLists/assetsSearchList';
import { getFormCategoriesLoadOptions as getFormCategoriesLoadOptionsFn } from 'utils/fetch/searchLists/formCategoriesSearchList';
import useTasksMultiselectQueryHook from 'utils/fetch/hooks/useTasksMultiselectQueryHook';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getLoadOptionsWithSelectedFilter } from '@lib/components/SelectPanel/utils/getLoadOptionsWithSelectedFilter';
import useFilterFields, {
  FieldNames as FilterFieldNames,
} from './utils/getFilterFields';
import getTasksColumns from './utils/getTasksColumns';
import { createAdditionalFilters } from './utils/createAdditionalFilters';

export { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';

type TasksModalTableMultiselectProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (values: string[]) => void;
  formRef: FormRefType;
  fieldName?: string;
  mode?: ModalTableMultiselectMode;
  scopeName?: TaskScopeNameEnum;
};

// CURRENT scope returns one row per recurring chain (latest occurrence),
// deduping by group_key on the BE side so the modal doesn't show duplicate
// rows for recurring tasks. Callers whose mutation only accepts a narrower
// task scope (e.g. Departments' taskLinks, which the BE validates against
// Task.active) must pass `scopeName` to keep the picker from offering tasks
// the save will then reject as invalid.
const DEFAULT_SCOPE_NAME = TaskScopeNameEnum.Current;

function TasksModalTableMultiselect({
  isOpen,
  onClose,
  onSelect,
  formRef,
  fieldName = 'taskIds',
  mode = ModalTableMultiselectMode.WithTabs,
  scopeName = DEFAULT_SCOPE_NAME,
}: TasksModalTableMultiselectProps) {
  const scope = useMemo(() => ({ name: scopeName }), [scopeName]);
  const { t } = useTranslation();
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);

  const selectedTasks = useSelectedFormFieldIds(formRef, fieldName);

  const filterStateDefault = useMemo(
    () => ({
      [FilterFieldNames.Sectors]: [],
      [FilterFieldNames.Sites]: [],
      [FilterFieldNames.Equipment]: [],
      [FilterFieldNames.FormCategories]: [],
    }),
    [],
  );

  const {
    activeTab,
    activeTabState,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isSelectedOnlyMode,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  } = useModalTableModeManager({
    isOpen,
    mode,
    selectedIds: selectedTasks,
    filterStateDefault,
  });

  const { shouldRunAllQuery, handleAllTabQueryResult, getAllTabSnapshot } =
    useModalTableAllTabManager<Task>({
      isOpen,
      mode,
      activeTab,
      isSelectedOnlyMode,
    });

  const searchQueryFilterSchema = [
    'name',
    'formCategoryName',
    'siteName',
    'siteAreaName',
    'siteCode',
    'siteAreaCode',
    'assetName',
    'assetCode',
    'priority',
  ];

  const additionalFilters = useMemo(() => {
    if (isSelectedOnlyMode || !filterState) return undefined;
    return createAdditionalFilters(filterState[0] || {});
  }, [filterState, isSelectedOnlyMode]);

  // All tab query
  const {
    collection,
    loading: tasksLoading,
    firstLoading: tasksFirstLoading,
    totalCount,
    fetchIds,
  } = useTasksMultiselectQueryHook({
    searchQuery: searchState?.[0] || '',
    searchQueryFilterSchema,
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    orderingParams: orderingState?.[0] || {},
    additionalFilters,
    scope,
    skip: !shouldRunAllQuery,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    handleAllTabQueryResult({
      collection,
      totalCount,
      loading: tasksLoading,
      firstLoading: tasksFirstLoading,
    });
  }, [
    collection,
    handleAllTabQueryResult,
    tasksFirstLoading,
    tasksLoading,
    totalCount,
  ]);

  // Documentation→Task links are keyed by `group_key` on the BE, so the
  // modal must expose `groupKey` (not the per-instance `id`) as the row
  // identity the parent form receives via `onSelect`.
  const keyTaskByGroup = useCallback(
    (task: Task): Task => ({ ...task, id: task.groupKey ?? task.id }),
    [],
  );

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = getAllTabSnapshot({
    collection: collection.map(keyTaskByGroup),
    totalCount,
    firstLoading: tasksFirstLoading,
  });

  const fetchIdsByGroupKey = useCallback(async () => {
    const response = await fetchIds();
    const items = _get(response, 'data.data.collection', []) as Array<{
      id: string;
      groupKey?: string | null;
    }>;
    const seen = new Set<string>();
    const dedupedCollection: Array<{ id: string }> = [];
    items.forEach(({ id, groupKey }) => {
      const key = groupKey ?? id;
      if (seen.has(key)) return;
      seen.add(key);
      dedupedCollection.push({ id: key });
    });
    return {
      ...response,
      data: {
        ...(response?.data ?? {}),
        data: { collection: dedupedCollection },
      },
    } as Awaited<ReturnType<typeof fetchIds>>;
  }, [fetchIds]);

  const additionalFiltersSelected = useMemo(() => {
    return createAdditionalFilters(filterStateSelected[0] || {});
  }, [filterStateSelected]);

  // Selected tab query — filters by groupKey IN idsForSelectedTabQuery, which
  // holds the recurring-chain group keys stored in the form state. This works
  // in both create mode (no documentationId) and edit mode, mirroring the
  // id-based approach used by Assets/SiteAreas.
  const {
    collection: collectionSelectedRaw,
    loading: tasksLoadingSelected,
    firstLoading: tasksFirstLoadingSelected,
    totalCount: totalCountSelectedRaw,
  } = useTasksMultiselectQueryHook({
    searchQuery: searchStateSelected[0],
    searchQueryFilterSchema,
    paginationParams: {
      ...orderingStateSelected[0],
      ...paginationStateSelected[0],
    },
    orderingParams: orderingStateSelected[0],
    additionalFilters: isSelectedOnlyMode
      ? undefined
      : additionalFiltersSelected,
    groupKeys: idsForSelectedTabQuery,
    scope,
    skip:
      !isOpen || !isSelectedTabActive || idsForSelectedTabQuery.length === 0,
    fetchPolicy: 'cache-first',
  });

  const collectionSelected = useMemo(
    () =>
      idsForSelectedTabQuery.length === 0
        ? []
        : collectionSelectedRaw.map(keyTaskByGroup),
    [collectionSelectedRaw, idsForSelectedTabQuery.length, keyTaskByGroup],
  );
  const totalCountSelected =
    idsForSelectedTabQuery.length === 0 ? 0 : totalCountSelectedRaw;

  // Sectors query
  const [fetchSectorsSearchListLazyQuery] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSectorsLoadOptions = getLoadOptionsWithSelectedFilter((filters) =>
    getSectorsLoadOptionsFn(fetchSectorsSearchListLazyQuery, filters),
  );

  // Sites query (filtered by sectors)
  const [fetchSitesSearchListLazyQuery] = useSitesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSitesLoadOptions: GetLoadOptions = getLoadOptionsWithSelectedFilter(
    (filters) =>
      getSitesLoadOptionsFn(fetchSitesSearchListLazyQuery, filters, {
        includeSector: true,
      }),
    (values) => {
      const sectors = _get(values, FilterFieldNames.Sectors) || [];
      const sectorIds = sectors.map(({ value }) => value) as string[];
      const filters: Array<Record<string, unknown>> = [];
      if (sectorIds?.length) {
        filters.push({
          sectorId: {
            grouping: FilterGroupingEnum.And,
            predicate: { in: sectorIds },
            filterGroup: 'sectorId',
          },
        });
      }
      return filters;
    },
  );

  // Equipment/Assets query
  const [fetchAssetsSearchListLazyQuery] = useAssetsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getEquipmentLoadOptions: GetLoadOptions =
    getLoadOptionsWithSelectedFilter(() =>
      getAssetsLoadOptionsFn(fetchAssetsSearchListLazyQuery),
    );

  // Form categories query
  const [fetchFormCategoriesLazyQuery] = useFormCategoriesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getFormCategoriesLoadOptions = getLoadOptionsWithSelectedFilter(
    (filters) =>
      getFormCategoriesLoadOptionsFn(fetchFormCategoriesLazyQuery, filters),
  );

  const columns = getTasksColumns({ t });
  const filterFormFields = useFilterFields({
    getSectorsLoadOptions,
    getSitesLoadOptions,
    getEquipmentLoadOptions,
    getFormCategoriesLoadOptions,
    clearFilter,
  });

  // SelectedOnly mode
  if (isSelectedOnlyMode) {
    return (
      <ModalTableMultiunselect<Task>
        caption={t('details-summary')}
        columns={columns}
        dataSelected={collectionSelected}
        firstLoadingSelected={tasksFirstLoadingSelected}
        isOpen={isOpen}
        loadingSelected={tasksLoadingSelected || isFetchingAllIds}
        onClose={onClose}
        onSelect={onSelect}
        onSelectedIdsChange={setSelectedIdsForQuery}
        orderingStateSelected={orderingStateSelected}
        paginationStateSelected={paginationStateSelected}
        searchPlaceholder={t('tasks-multiselect-search-placeholder')}
        searchStateSelected={searchStateSelected}
        selected={selectedTasks}
        totalEntriesSelected={totalCountSelected}
      />
    );
  }

  // WithTabs mode
  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  return (
    <ModalTableMultiselect<Task>
      activeTabState={activeTabState}
      caption={t('task-multiselect')}
      columns={columns}
      data={allTabCollection}
      dataSelected={collectionSelected}
      fetchIds={fetchIdsByGroupKey}
      filterFormFields={filterFormFields}
      filterState={filterState}
      filterStateSelected={filterStateSelected}
      firstLoading={allTabFirstLoading}
      firstLoadingSelected={tasksFirstLoadingSelected}
      isOpen={isOpen}
      loading={shouldRunAllQuery ? tasksLoading : false}
      loadingSelected={tasksLoadingSelected || isFetchingAllIds}
      onClose={onClose}
      onIsFetchingAllIdsChange={setIsFetchingAllIds}
      onSelect={onSelect}
      onSelectedIdsChange={setSelectedIdsForQuery}
      orderingState={orderingState}
      orderingStateSelected={orderingStateSelected}
      paginationState={paginationState}
      paginationStateSelected={paginationStateSelected}
      searchPleceholder={t('tasks-multiselect-search-placeholder')}
      searchState={searchState}
      searchStateSelected={searchStateSelected}
      selected={selectedTasks}
      totalEntries={allTabTotalCount}
      totalEntriesSelected={totalCountSelected}
    />
  );
}

export default memo(TasksModalTableMultiselect);
